import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api';
import BadgeUploader from './BadgeUploader';
import './ClubEditor.css';

const EMPTY_FORM = {
  slug: '',
  name: '',
  description: '',
  is_active: true,
  join_mode: 'open',
  owner_user_id: '',
};

function ClubEditor({
  canAdminAllClubs = false,
  ownerOnly = false,
  fixedClubId = null,
  hideClubList = false,
}) {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerSuggestions, setOwnerSuggestions] = useState([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3500);
  };

  const loadClubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/admin/clubs');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load clubs');
      setClubs(data.clubs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMembers = useCallback(async (clubId) => {
    if (!clubId) {
      setMembers([]);
      return;
    }
    setMembersLoading(true);
    try {
      const res = await apiFetch(`/admin/clubs/${clubId}/members`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setMembers(data.members || []);
    } catch {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const loadJoinRequests = useCallback(async (clubId) => {
    if (!clubId) {
      setJoinRequests([]);
      return;
    }
    setRequestsLoading(true);
    try {
      const res = await apiFetch(`/admin/clubs/${clubId}/requests`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) setJoinRequests(data.requests || []);
    } catch {
      setJoinRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClubs();
  }, [loadClubs]);

  useEffect(() => {
    if (editingId) {
      loadMembers(editingId);
      loadJoinRequests(editingId);
    } else {
      setMembers([]);
      setJoinRequests([]);
    }
  }, [editingId, loadMembers, loadJoinRequests]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setMembers([]);
    setMemberSearch('');
    setMemberSuggestions([]);
    setSelectedMember(null);
    setOwnerSearch('');
    setOwnerSuggestions([]);
    setSelectedOwner(null);
  };

  const startEdit = (club) => {
    setEditingId(club.id);
    setForm({
      slug: club.slug || '',
      name: club.name || '',
      description: club.description || '',
      is_active: club.is_active !== false,
      join_mode: club.join_mode || 'open',
      owner_user_id: club.owner_user_id ? String(club.owner_user_id) : '',
    });
    if (club.owner_user_id) {
      setSelectedOwner({
        id: club.owner_user_id,
        username: club.owner_username || `User #${club.owner_user_id}`,
      });
      setOwnerSearch(club.owner_username || '');
    } else {
      setSelectedOwner(null);
      setOwnerSearch('');
    }
    setOwnerSuggestions([]);
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    if (!ownerOnly || !fixedClubId || !clubs.length) return;
    const owned = clubs.find((c) => Number(c.id) === Number(fixedClubId));
    if (owned && Number(editingId) !== Number(fixedClubId)) {
      startEdit(owned);
    }
  }, [ownerOnly, fixedClubId, clubs, editingId]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slug.trim() || !form.name.trim()) {
      setError('Slug and name are required');
      return;
    }

    setSaving(true);
    setError(null);

    const body = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      is_active: form.is_active,
      join_mode: form.join_mode,
    };
    if (canAdminAllClubs) {
      body.owner_user_id = selectedOwner?.id ? Number(selectedOwner.id) : null;
    }

    try {
      const res = editingId
        ? await apiFetch(`/admin/clubs/${editingId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        : await apiFetch('/admin/clubs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to save club');

      flash(editingId ? 'Club updated' : 'Club created');
      if (!editingId && data.club?.id) {
        startEdit(data.club);
      }
      await loadClubs();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/admin/clubs/${deleteTarget.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to delete club');
      flash(`Deleted ${deleteTarget.name}`);
      if (editingId === deleteTarget.id) resetForm();
      setDeleteTarget(null);
      await loadClubs();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const q = memberSearch.trim();
    if (selectedMember && selectedMember.username === q) {
      setMemberSuggestions([]);
      return undefined;
    }
    if (q.length < 2) {
      setMemberSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setMemberSearchLoading(true);
      try {
        const res = await apiFetch(`/admin/users/search?q=${encodeURIComponent(q)}`);
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          const memberIds = new Set(members.map((m) => Number(m.user_id)));
          setMemberSuggestions((data.users || []).filter((u) => !memberIds.has(Number(u.id))));
        }
      } catch {
        setMemberSuggestions([]);
      } finally {
        setMemberSearchLoading(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [memberSearch, members, selectedMember]);

  useEffect(() => {
    if (!canAdminAllClubs) return undefined;
    const q = ownerSearch.trim();
    if (selectedOwner && selectedOwner.username === q) {
      setOwnerSuggestions([]);
      return undefined;
    }
    if (q.length < 2) {
      setOwnerSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setOwnerSearchLoading(true);
      try {
        const res = await apiFetch(`/admin/users/search?q=${encodeURIComponent(q)}`);
        const data = await res.json().catch(() => ({}));
        if (res.ok) setOwnerSuggestions(data.users || []);
      } catch {
        setOwnerSuggestions([]);
      } finally {
        setOwnerSearchLoading(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [ownerSearch, selectedOwner, canAdminAllClubs]);

  const handleAddMember = async () => {
    const userId = selectedMember?.id;
    if (!editingId || !userId) {
      setError('Search for a player by username and select them from the list');
      return;
    }
    setError(null);
    try {
      const res = await apiFetch(`/admin/clubs/${editingId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to add member');
      setMemberSearch('');
      setMemberSuggestions([]);
      setSelectedMember(null);
      flash(data.message || 'Invite sent');
      await loadMembers(editingId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleForceAddMember = async () => {
    const userId = selectedMember?.id;
    if (!editingId || !userId) return;
    setError(null);
    try {
      const res = await apiFetch(`/admin/clubs/${editingId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, force: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to add member');
      setMemberSearch('');
      setMemberSuggestions([]);
      setSelectedMember(null);
      flash('Member added');
      await loadMembers(editingId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReviewRequest = async (requestId, action) => {
    if (!editingId) return;
    try {
      const res = await apiFetch(`/admin/clubs/${editingId}/requests/${requestId}/${action}`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to update request');
      flash(action === 'accept' ? 'Request accepted' : 'Request declined');
      await loadJoinRequests(editingId);
      await loadMembers(editingId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!editingId) return;
    try {
      const res = await apiFetch(`/admin/clubs/${editingId}/members/${userId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to remove member');
      flash('Member removed');
      await loadMembers(editingId);
    } catch (err) {
      setError(err.message);
    }
  };

  const editingClub = editingId ? clubs.find((c) => Number(c.id) === Number(editingId)) : null;

  return (
    <div className="club-editor-section">
      <h2>{ownerOnly ? 'Manage your club' : 'Club Manager'}</h2>
      <p className="club-editor-hint">
        {ownerOnly
          ? 'Edit your club settings, review join requests, and invite members. Each player may belong to one club.'
          : 'Create and edit clubs, upload badges, and manage members. Each player may belong to one club per game type.'}
      </p>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="club-editor-layout">
        <form className="club-editor-form" onSubmit={handleSubmit}>
          <h3>{editingId ? 'Edit club' : 'Add club'}</h3>

          {ownerOnly && (
            <p className="club-editor-note">You can manage only the club you own.</p>
          )}

          {!editingId && !canAdminAllClubs && !ownerOnly && (
            <p className="club-editor-note">You need admin_clubs permission to create new clubs.</p>
          )}

          <label className="club-field">
            <span>Slug</span>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => handleFieldChange('slug', e.target.value)}
              placeholder="my-club"
              required
              disabled={!!editingId && !canAdminAllClubs}
            />
          </label>

          <label className="club-field">
            <span>Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              required
            />
          </label>

          <label className="club-field">
            <span>Description</span>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </label>

          {canAdminAllClubs && (
            <div className="club-owner-panel">
              <label className="club-field club-add-member-field">
                <span>Club owner (optional)</span>
                <input
                  type="search"
                  autoComplete="off"
                  placeholder="Search by username…"
                  value={ownerSearch}
                  onChange={(e) => {
                    setOwnerSearch(e.target.value);
                    setSelectedOwner(null);
                  }}
                />
              </label>
              <p className="club-editor-note">Owner must already be a club member. Leave blank for no owner.</p>
              {ownerSearchLoading && (
                <p className="club-member-search-status">Searching…</p>
              )}
              {ownerSuggestions.length > 0 && !selectedOwner && (
                <ul className="club-member-suggestions">
                  {ownerSuggestions.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        className="club-member-suggestion-btn"
                        onClick={() => {
                          setSelectedOwner({ id: u.id, username: u.username });
                          setOwnerSearch(u.username);
                          setOwnerSuggestions([]);
                        }}
                      >
                        {u.username}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {selectedOwner && (
                <p className="club-member-selected">
                  Owner: <strong>{selectedOwner.username}</strong>
                  {' '}
                  <button
                    type="button"
                    className="club-btn club-btn--small club-btn--secondary"
                    onClick={() => {
                      setSelectedOwner(null);
                      setOwnerSearch('');
                      setOwnerSuggestions([]);
                    }}
                  >
                    Clear
                  </button>
                </p>
              )}
            </div>
          )}

          <label className="club-field">
            <span>Join policy</span>
            <select
              value={form.join_mode}
              onChange={(e) => handleFieldChange('join_mode', e.target.value)}
            >
              <option value="open">Open — anyone can join instantly</option>
              <option value="request">Request — players apply, you approve</option>
              <option value="closed">Closed — invite only</option>
            </select>
          </label>

          {canAdminAllClubs && (
            <label className="club-checkbox">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
              />
              <span>Active club (disabled clubs are hidden from browse/join)</span>
            </label>
          )}

          {editingClub && (
            <BadgeUploader
              kind="club"
              entityId={editingClub.id}
              entity={editingClub}
              canEdit
              label="Club chat badge"
              onUpdated={(updated) => {
                setClubs((prev) => prev.map((c) => (
                  Number(c.id) === Number(updated.id) ? { ...c, ...updated } : c
                )));
                flash('Club badge updated');
              }}
              onError={setError}
            />
          )}

          {editingId && (
            <div className="club-requests-panel">
              <h4>Join requests</h4>
              {requestsLoading ? (
                <p>Loading requests…</p>
              ) : joinRequests.length === 0 ? (
                <p className="club-members-empty">No pending requests</p>
              ) : (
                <ul className="club-members-list">
                  {joinRequests.map((r) => (
                    <li key={r.id}>
                      <span>{r.username}</span>
                      <button
                        type="button"
                        className="club-btn club-btn--small"
                        onClick={() => handleReviewRequest(r.id, 'accept')}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="club-btn club-btn--small club-btn--secondary"
                        onClick={() => handleReviewRequest(r.id, 'decline')}
                      >
                        Decline
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {editingId && (
            <div className="club-members-panel">
              <h4>Members</h4>
              {membersLoading ? (
                <p>Loading members…</p>
              ) : (
                <ul className="club-members-list">
                  {members.map((m) => (
                    <li key={m.user_id}>
                      <span>{m.username}</span>
                      <span className="club-member-role">{m.member_role}</span>
                      <button
                        type="button"
                        className="club-btn club-btn--small"
                        onClick={() => handleRemoveMember(m.user_id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                  {members.length === 0 && <li className="club-members-empty">No members yet</li>}
                </ul>
              )}
              <div className="club-add-member">
                <label className="club-field club-add-member-field">
                  <span>Send invite by username</span>
                  <input
                    type="search"
                    autoComplete="off"
                    placeholder="Start typing a username…"
                    value={memberSearch}
                    onChange={(e) => {
                      setMemberSearch(e.target.value);
                      setSelectedMember(null);
                    }}
                  />
                </label>
                {memberSearchLoading && (
                  <p className="club-member-search-status">Searching…</p>
                )}
                {memberSuggestions.length > 0 && !selectedMember && (
                  <ul className="club-member-suggestions">
                    {memberSuggestions.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          className="club-member-suggestion-btn"
                          onClick={() => {
                            setSelectedMember({ id: u.id, username: u.username });
                            setMemberSearch(u.username);
                            setMemberSuggestions([]);
                          }}
                        >
                          {u.username}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {selectedMember && (
                  <p className="club-member-selected">
                    Selected: <strong>{selectedMember.username}</strong>
                  </p>
                )}
                <button
                  type="button"
                  className="club-btn"
                  onClick={handleAddMember}
                  disabled={!selectedMember}
                >
                  Send invite
                </button>
                {canAdminAllClubs && (
                  <button
                    type="button"
                    className="club-btn club-btn--secondary"
                    onClick={handleForceAddMember}
                    disabled={!selectedMember}
                  >
                    Add directly (admin)
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="club-form-actions">
            {(editingId || canAdminAllClubs || ownerOnly) && (
              <button type="submit" className="club-btn" disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add club'}
              </button>
            )}
            {editingId && !ownerOnly && (
              <button type="button" className="club-btn club-btn--secondary" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>

        {!hideClubList && (
        <div className="club-list-panel">
          <h3>Clubs</h3>
          {loading ? (
            <p>Loading…</p>
          ) : clubs.length === 0 ? (
            <p className="club-empty">No clubs yet.</p>
          ) : (
            <ul className="club-admin-list">
              {clubs.map((club) => (
                <li key={club.id} className="club-admin-item">
                  <div className="club-admin-item-head">
                    <strong>{club.name}</strong>
                    <span className="club-admin-slug">{club.slug}</span>
                  </div>
                  {club.description && <p className="club-admin-desc">{club.description}</p>}
                  <div className="club-admin-meta">
                    {club.member_count} member{club.member_count === 1 ? '' : 's'}
                    {!club.is_active ? ' · inactive' : ''}
                  </div>
                  <div className="club-admin-actions">
                    <button type="button" className="club-btn" onClick={() => startEdit(club)}>
                      Edit
                    </button>
                    {canAdminAllClubs && (
                      <button
                        type="button"
                        className="club-btn club-btn--danger"
                        onClick={() => setDeleteTarget(club)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </div>

      {deleteTarget && (
        <div
          className="club-delete-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
          role="presentation"
        >
          <div className="club-delete-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3>Delete club?</h3>
            <p>Delete <strong>{deleteTarget.name}</strong>? Members will be removed.</p>
            <div className="club-form-actions">
              <button type="button" className="club-btn club-btn--danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button type="button" className="club-btn club-btn--secondary" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClubEditor;